import * as THREE from 'three';

// «نقشه‌ی نور»ِ خانه: هر سطح باید جای مخصوصِ خودش را در یک تصویرِ مشترک داشته باشد (UVِ دوم = uv1).
//
// ساختمان تقریباً همه‌اش سطحِ صاف است (دیوار، کف، سقف، قاب)، پس روشِ ساده و دقیق این است:
//   ۱) مثلث‌های هم‌جوارِ هم‌جهت (هم‌صفحه یا تقریباً هم‌صفحه) یک «تکه» (chart) می‌شوند
//   ۲) هر تکه روی صفحه‌ی خودش تصویر می‌شود — با مقیاسِ واقعی (متر)، پس همه‌جا تراکمِ نقطه‌ها یکسان است
//   ۳) تکه‌ها مثلِ قفسه کنارِ هم چیده می‌شوند و بزرگ‌ترین تراکمی که جا می‌شود پیدا می‌شود
// دورِ هر تکه چند نقطه فاصله‌ی خالی هست تا نورِ یک تکه روی تکه‌ی کناری نریزد (بعداً با «گسترش» پر می‌شود).

export const ATLAS_PAD = 2; // نقطه‌ی خالی در هر طرفِ هر تکه
const NORMAL_TOLERANCE = 0.97; // cos ~14° — انحنای ملایم در یک تکه
const MAX_DENSITY = 256; // نقطه در متر (≈ ۴ میلی‌متر) — بیشتر از این فایده‌ای ندارد
const MIN_DENSITY = 4;

function quantKey(x, y, z) {
  // موقعیتِ رأس با دقتِ ۰٫۱ میلی‌متر — برای پیدا کردنِ مثلث‌های هم‌جوار
  return `${Math.round(x * 10000)},${Math.round(y * 10000)},${Math.round(z * 10000)}`;
}

function buildCharts(mesh) {
  const geo = mesh.geometry;
  const pos = geo.attributes.position;
  const triCount = Math.floor(pos.count / 3);
  const m = mesh.matrixWorld;
  const world = new Float32Array(pos.count * 3);
  const v = new THREE.Vector3();
  for (let i = 0; i < pos.count; i += 1) {
    v.fromBufferAttribute(pos, i).applyMatrix4(m);
    world[i * 3] = v.x;
    world[i * 3 + 1] = v.y;
    world[i * 3 + 2] = v.z;
  }
  const normals = new Float32Array(triCount * 3);
  const areas = new Float32Array(triCount);
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const c = new THREE.Vector3();
  const n = new THREE.Vector3();
  for (let t = 0; t < triCount; t += 1) {
    a.fromArray(world, t * 9);
    b.fromArray(world, t * 9 + 3);
    c.fromArray(world, t * 9 + 6);
    n.subVectors(b, a).cross(c.clone().sub(a));
    const len = n.length();
    areas[t] = len * 0.5;
    if (len > 1e-12) n.divideScalar(len);
    else n.set(0, 1, 0);
    normals[t * 3] = n.x;
    normals[t * 3 + 1] = n.y;
    normals[t * 3 + 2] = n.z;
  }

  // هم‌جواری از روی لبه‌های مشترک
  const vertKey = new Array(pos.count);
  for (let i = 0; i < pos.count; i += 1) vertKey[i] = quantKey(world[i * 3], world[i * 3 + 1], world[i * 3 + 2]);
  const edgeMap = new Map();
  for (let t = 0; t < triCount; t += 1) {
    for (let e = 0; e < 3; e += 1) {
      const k1 = vertKey[t * 3 + e];
      const k2 = vertKey[t * 3 + ((e + 1) % 3)];
      const key = k1 < k2 ? `${k1}|${k2}` : `${k2}|${k1}`;
      let list = edgeMap.get(key);
      if (!list) {
        list = [];
        edgeMap.set(key, list);
      }
      list.push(t);
    }
  }
  const neighbors = Array.from({ length: triCount }, () => []);
  edgeMap.forEach((list) => {
    for (let i = 0; i < list.length; i += 1) {
      for (let j = i + 1; j < list.length; j += 1) {
        neighbors[list[i]].push(list[j]);
        neighbors[list[j]].push(list[i]);
      }
    }
  });

  const chartOf = new Int32Array(triCount).fill(-1);
  const charts = [];
  const degenerate = [];
  for (let seed = 0; seed < triCount; seed += 1) {
    if (chartOf[seed] !== -1) continue;
    if (areas[seed] < 1e-10) {
      chartOf[seed] = -2;
      degenerate.push(seed);
      continue;
    }
    const sn = new THREE.Vector3(normals[seed * 3], normals[seed * 3 + 1], normals[seed * 3 + 2]);
    const id = charts.length;
    const tris = [seed];
    chartOf[seed] = id;
    const queue = [seed];
    while (queue.length) {
      const t = queue.pop();
      for (const nb of neighbors[t]) {
        if (chartOf[nb] !== -1 || areas[nb] < 1e-10) continue;
        const d = sn.x * normals[nb * 3] + sn.y * normals[nb * 3 + 1] + sn.z * normals[nb * 3 + 2];
        if (d < NORMAL_TOLERANCE) continue;
        chartOf[nb] = id;
        tris.push(nb);
        queue.push(nb);
      }
    }
    // محورهای صفحه‌ی تکه: برای دیوارها «افقی/عمودی»، برای کف/سقف «x/z»
    const helper = Math.abs(sn.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
    const u = new THREE.Vector3().crossVectors(helper, sn).normalize();
    const w = new THREE.Vector3().crossVectors(sn, u).normalize();
    let umin = Infinity;
    let umax = -Infinity;
    let vmin = Infinity;
    let vmax = -Infinity;
    const coords = new Float32Array(tris.length * 6);
    tris.forEach((t, i) => {
      for (let k = 0; k < 3; k += 1) {
        const vi = t * 3 + k;
        const px = world[vi * 3];
        const py = world[vi * 3 + 1];
        const pz = world[vi * 3 + 2];
        const cu = px * u.x + py * u.y + pz * u.z;
        const cv = px * w.x + py * w.y + pz * w.z;
        coords[i * 6 + k * 2] = cu;
        coords[i * 6 + k * 2 + 1] = cv;
        if (cu < umin) umin = cu;
        if (cu > umax) umax = cu;
        if (cv < vmin) vmin = cv;
        if (cv > vmax) vmax = cv;
      }
    });
    charts.push({ mesh, tris, coords, umin, vmin, w: umax - umin, h: vmax - vmin, x: 0, y: 0, cw: 0, ch: 0 });
  }
  return { charts, degenerate, triCount };
}

function chartTexels(chart, density) {
  chart.cw = Math.max(1, Math.ceil(chart.w * density)) + 1 + ATLAS_PAD * 2;
  chart.ch = Math.max(1, Math.ceil(chart.h * density)) + 1 + ATLAS_PAD * 2;
}

// چیدنِ قفسه‌ای؛ ارتفاعِ مصرف‌شده را برمی‌گرداند (یا Infinity اگر تکه‌ای از عرض بزرگ‌تر باشد)
function shelfPack(charts, density, size) {
  charts.forEach((c) => chartTexels(c, density));
  const order = charts.slice().sort((p, q) => q.ch - p.ch || q.cw - p.cw);
  let x = 0;
  let y = 0;
  let shelfH = 0;
  for (const c of order) {
    if (c.cw > size) return Infinity;
    if (x + c.cw > size) {
      y += shelfH;
      x = 0;
      shelfH = 0;
    }
    c.x = x;
    c.y = y;
    x += c.cw;
    if (c.ch > shelfH) shelfH = c.ch;
  }
  return y + shelfH;
}

// meshes: مش‌هایی که نورشان پخته می‌شود (matrixWorld به‌روز). هندسه‌ی هر مش بی‌ایندکس می‌شود (هر مثلث رأس‌های
// خودش را دارد — لازم است چون یک رأس در دو تکه‌ی مختلف جای متفاوت دارد) و ویژگیِ uv1 می‌گیرد.
export function buildLightmapAtlas(meshes, size) {
  const all = [];
  const perMesh = [];
  for (const mesh of meshes) {
    if (mesh.geometry.index) {
      const old = mesh.geometry;
      mesh.geometry = old.toNonIndexed();
    } else {
      mesh.geometry = mesh.geometry.clone();
    }
    const r = buildCharts(mesh);
    perMesh.push({ mesh, ...r });
    all.push(...r.charts);
  }
  if (!all.length) return { density: 0, usedHeight: 0, chartCount: 0 };

  // بزرگ‌ترین تراکمی که در تصویر جا می‌شود (جست‌وجوی دودویی)
  let lo = MIN_DENSITY;
  let hi = MAX_DENSITY;
  if (shelfPack(all, lo, size) > size) {
    // حتی با کم‌ترین تراکم جا نمی‌شود — تراکم را پایین‌تر می‌آوریم
    while (lo > 0.25 && shelfPack(all, lo, size) > size) lo *= 0.7;
    hi = lo;
  }
  if (shelfPack(all, hi, size) <= size) lo = hi;
  for (let i = 0; i < 18 && hi - lo > 0.05; i += 1) {
    const mid = (lo + hi) / 2;
    if (shelfPack(all, mid, size) <= size) lo = mid;
    else hi = mid;
  }
  const density = lo;
  const usedHeight = Math.min(size, shelfPack(all, density, size));

  for (const { mesh, charts, degenerate, triCount } of perMesh) {
    const uv1 = new Float32Array(triCount * 3 * 2);
    for (const c of charts) {
      const ox = c.x + ATLAS_PAD + 0.5;
      const oy = c.y + ATLAS_PAD + 0.5;
      c.tris.forEach((t, i) => {
        for (let k = 0; k < 3; k += 1) {
          const cu = c.coords[i * 6 + k * 2];
          const cv = c.coords[i * 6 + k * 2 + 1];
          uv1[(t * 3 + k) * 2] = (ox + (cu - c.umin) * density) / size;
          uv1[(t * 3 + k) * 2 + 1] = (oy + (cv - c.vmin) * density) / size;
        }
      });
    }
    // مثلث‌های بی‌مساحت (دیده نمی‌شوند) — یک نقطه‌ی ثابت از اولین تکه
    if (degenerate.length) {
      const c = charts[0];
      const fx = c ? (c.x + ATLAS_PAD + 0.5) / size : 0;
      const fy = c ? (c.y + ATLAS_PAD + 0.5) / size : 0;
      for (const t of degenerate) {
        for (let k = 0; k < 3; k += 1) {
          uv1[(t * 3 + k) * 2] = fx;
          uv1[(t * 3 + k) * 2 + 1] = fy;
        }
      }
    }
    mesh.geometry.setAttribute('uv1', new THREE.BufferAttribute(uv1, 2));
  }
  return { density, usedHeight, chartCount: all.length };
}
